import type { UnifiedIconName } from './ui/unified-icon'

export interface DashboardNavItem {
	icon: UnifiedIconName
	name: string
	url: string
	badge?: string | number
	isActive?: boolean
	/**
	 * Single required permission. Kept for backward compat — prefer
	 * `requirePermission` (array) for new items. Interpreted as "user must
	 * have this permission OR `full_access`".
	 */
	permission?: string
	/**
	 * User must have at least one of these permission names (OR semantics).
	 * `full_access` always satisfies any permission gate.
	 */
	requirePermission?: string[]
	/**
	 * User must have at least one of these role names (OR semantics).
	 * Used for items that should be role-restricted regardless of
	 * granular permissions (e.g. "Roles & Permissions" → `tenant_owner`).
	 */
	requireRole?: string[]
	category?: string
	comingSoon?: boolean
	/**
	 * Optional child nav items, rendered as a sub-menu below this item when the
	 * current route is within this item's `url` (e.g. the Billing dashboard's
	 * sections). Children inherit the parent's visibility gate.
	 */
	children?: DashboardNavItem[]
}

// Each nav item declares its own `requireRole` / `requirePermission` gate.
// Apex roles (superadmin, tenant_owner) are expected to hold either the
// `full_access` permission or the relevant role tag — the seeders in
// `app/models/landlord-roles.server.ts` and `app/models/platform-roles.server.ts`
// guarantee this. Custom roles get filtered correctly without a bypass.

// ─────────────────────────────────────────────────────────────
// SuperAdmin navigation
// ─────────────────────────────────────────────────────────────

export const superAdminNavItems: DashboardNavItem[] = [
	{
		icon: 'dashboard-icon',
		name: 'Super Dashboard',
		url: '/dashboard',
		requireRole: ['superadmin'],
		category: 'main',
	},
	{
		icon: 'tenent-user',
		name: 'Landlord Organizations',
		url: '/superadmin/tenants',
		requirePermission: ['tenant_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'users',
		name: 'All Users',
		url: '/superadmin/users',
		requirePermission: ['user_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'admin-user',
		name: 'Platform Staff',
		url: '/superadmin/admins',
		requirePermission: ['user_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'role-manage',
		name: 'Roles & Permissions',
		url: '/superadmin/roles',
		requirePermission: ['role_management', 'full_access'],
		category: 'management',
	},
	{
		icon: 'dashboard-icon-holiday_village',
		name: 'Properties',
		url: '/properties',
		requirePermission: ['full_access'],
		category: 'management',
	},
	{
		icon: 'file-text',
		name: 'All Applications',
		url: '/superadmin/applications',
		requirePermission: ['full_access'],
		category: 'management',
	},
	{
		icon: 'system-setting',
		name: 'System Settings',
		url: '/superadmin/settings',
		requirePermission: ['full_access'],
		category: 'settings',
	},
	{
		icon: 'dashboard-icon',
		name: 'Billing & Credits',
		url: '/superadmin/billing',
		requirePermission: ['full_access'],
		category: 'settings',
		children: [
			{ icon: 'dashboard-icon', name: 'Overview', url: '/superadmin/billing' },
			{
				icon: 'tenent-user',
				name: 'Landlords',
				url: '/superadmin/billing/landlords',
			},
			{
				icon: 'file-text',
				name: 'Transactions',
				url: '/superadmin/billing/transactions',
			},
			{
				icon: 'dashboard-icon',
				name: 'Usage',
				url: '/superadmin/billing/usage',
			},
			{
				icon: 'system-setting',
				name: 'Operations',
				url: '/superadmin/billing/operations',
			},
			{
				icon: 'system-setting',
				name: 'Settings',
				url: '/superadmin/billing/settings',
			},
		],
	},
	{
		icon: 'envelope-closed',
		name: 'Notifications',
		url: '/superadmin/notifications',
		requirePermission: ['full_access'],
		category: 'settings',
	},
]

// ─────────────────────────────────────────────────────────────
// Admin (platform-admin-but-not-super) navigation
// ─────────────────────────────────────────────────────────────

export const adminNavItems: DashboardNavItem[] = [
	{
		icon: 'dashboard-icon-home',
		name: 'Dashboard',
		url: '/dashboard',
		requireRole: ['admin'],
		category: 'main',
	},
	{
		// Portfolio-wide application view for admins (same surface tenant_owners
		// see). The loader on /landlord/applications already accepts the
		// `admin` role, so this just exposes it in the sidebar.
		icon: 'file-text',
		name: 'All Applications',
		url: '/landlord/applications',
		requireRole: ['admin'],
		category: 'main',
	},
	{
		icon: 'settings',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'question-mark-circled',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
]

// ─────────────────────────────────────────────────────────────
// Landlord navigation — gated against the real 8 landlord permissions
// declared in app/utils/landlord-permissions.ts
// ─────────────────────────────────────────────────────────────

export const landlordNavItems: DashboardNavItem[] = [
	{
		icon: 'dashboard-icon-home',
		name: 'Dashboard',
		url: '/dashboard',
		category: 'main',
	},
	{
		// Portfolio-wide application view — visible to tenant_owner (and the
		// legacy `landlord` role alias still used by some seeded accounts) plus
		// the read-only `viewer` role. Property managers / leasing agents see
		// per-property lists via the property detail page
		// (`/properties/:id/applications`).
		icon: 'files',
		name: 'All Applications',
		url: '/landlord/applications',
		requireRole: ['tenant_owner', 'landlord', 'viewer'],
		category: 'main',
	},
	{
		icon: 'users',
		name: 'User Management',
		url: '/landlord/settings/team',
		requirePermission: ['users.edit_assign'],
		category: 'management',
	},
	{
		// Property portfolio (TT-54, dashboard-v2 design). No nav gate: every
		// landlord-org role may open the list — the server scopes it (property-
		// scoped roles only see assigned properties). The design's "AI Lease"
		// and "Voice / SMS Agent" items are DELIBERATELY absent until those
		// modules exist (IMPLEMENTATION-PLAN.md §2.3 — no dead nav).
		icon: 'properties',
		name: 'Properties',
		url: '/properties',
		category: 'main',
	},
	{
		icon: 'role-manage',
		name: 'Roles & Permissions',
		url: '/landlord/settings/roles',
		// Permission-gated: anyone holding `roles.manage` can reshape the role
		// matrix. Default-granted to tenant_owner only; can be delegated to other
		// roles (e.g. property_manager) by editing role permissions.
		requirePermission: ['roles.manage'],
		category: 'settings',
	},
	{
		// Personal account settings (profile + security). Shared across all roles
		// at /settings — the guard (requireUser) and scope (the signed-in user's
		// own account) are identical for everyone, so there's no role boundary to
		// justify a landlord-specific route. Role-specific pages (notifications,
		// org, roles, team) keep their own /landlord/settings/* routes.
		icon: 'settings',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'question-mark-circled',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
	{
		icon: 'file-text',
		name: 'Audit Log',
		url: '/landlord/settings/audit-log',
		requireRole: ['tenant_owner'],
		category: 'settings',
	},
	{
		// Billing & credits — owner-only, like Audit Log / Notifications. A finer
		// `billing.manage` permission for delegation can be added later.
		icon: 'system-setting',
		name: 'Billing & Credits',
		url: '/landlord/settings/billing',
		requireRole: ['tenant_owner'],
		category: 'settings',
	},
	{
		icon: 'envelope-closed',
		name: 'Notifications',
		url: '/landlord/settings/notifications',
		requireRole: ['tenant_owner'],
		category: 'settings',
	},
]

// ─────────────────────────────────────────────────────────────
// Tenant navigation
// ─────────────────────────────────────────────────────────────

export const tenantNavItems: DashboardNavItem[] = [
	{
		icon: 'dashboard-icon-home',
		name: 'Dashboard',
		url: '/dashboard',
		category: 'main',
	},
	{
		icon: 'dashboard-icon-description',
		name: 'Applications',
		url: '/applications',
		category: 'applications',
	},
	{
		icon: 'settings',
		name: 'Settings',
		url: '/settings',
		category: 'settings',
	},
	{
		icon: 'question-mark-circled',
		name: 'Help & Support',
		url: '/help-support',
		category: 'support',
	},
]

// Secondary navigation items (common for all)
export const secondaryNavItems: DashboardNavItem[] = []

// ─────────────────────────────────────────────────────────────
// Filtering
// ─────────────────────────────────────────────────────────────

function hasRequiredRole(item: DashboardNavItem, userRoles: string[]): boolean {
	if (!item.requireRole || item.requireRole.length === 0) return true
	return item.requireRole.some((r) => userRoles.includes(r))
}

function hasRequiredPermission(
	item: DashboardNavItem,
	userPermissions: string[],
): boolean {
	const needed =
		item.requirePermission ?? (item.permission ? [item.permission] : [])
	if (needed.length === 0) return true
	if (userPermissions.includes('full_access')) return true
	return needed.some((p) => userPermissions.includes(p))
}

/**
 * Return the list of nav items visible to a user.
 *
 * An item is visible iff **every** gate it declares is satisfied:
 *   - `requireRole`   → user has at least one listed role
 *   - `requirePermission` / `permission` → user has at least one listed
 *     permission (or holds `full_access`)
 *
 * No role-based bypass — superadmin and tenant_owner pass gates because
 * they hold `full_access` (superadmin) or the role tags listed on each
 * item (tenant_owner). Custom roles are filtered exactly by their grants.
 */
function filterItems(
	items: DashboardNavItem[],
	userRoles: string[],
	userPermissions: string[],
): DashboardNavItem[] {
	return items.filter(
		(item) =>
			hasRequiredRole(item, userRoles) &&
			hasRequiredPermission(item, userPermissions),
	)
}

/**
 * Get the nav set for the user's primary role, filtered by role + permission
 * gates. Uses userType as primary selector for custom role support.
 */
export function getDashboardNavItems(
	userRoles: string[],
	userPermissions: string[] = [],
	userType?: string,
): DashboardNavItem[] {
	if (userRoles.includes('superadmin')) {
		return filterItems(superAdminNavItems, userRoles, userPermissions)
	}
	if (userRoles.includes('admin')) {
		return filterItems(adminNavItems, userRoles, userPermissions)
	}
	// Use userType as primary selector; fall back to role name list for backward compat
	const isLandlordSide =
		userType === 'landlord' ||
		userRoles.includes('landlord') ||
		userRoles.includes('tenant_owner') ||
		userRoles.includes('property_manager') ||
		userRoles.includes('leasing_agent') ||
		userRoles.includes('viewer')
	if (isLandlordSide) {
		return filterItems(landlordNavItems, userRoles, userPermissions)
	}
	return filterItems(tenantNavItems, userRoles, userPermissions)
}

// Group navigation items by category
export function groupNavItemsByCategory(
	items: DashboardNavItem[],
): Record<string, DashboardNavItem[]> {
	return items.reduce(
		(groups, item) => {
			const category = item.category || 'other'
			if (!groups[category]) {
				groups[category] = []
			}
			groups[category].push(item)
			return groups
		},
		{} as Record<string, DashboardNavItem[]>,
	)
}

// Get role display information
export function getRoleDisplayInfo(role: string): {
	name: string
	color: string
	icon: UnifiedIconName
} {
	const roleInfo = {
		superadmin: {
			name: 'Super Admin',
			color: 'red',
			icon: 'shield-check' as UnifiedIconName,
		},
		admin: {
			name: 'Admin',
			color: 'blue',
			icon: 'user-check' as UnifiedIconName,
		},
		landlord: {
			name: 'Landlord',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		tenant_owner: {
			name: 'Landlord',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		property_manager: {
			name: 'Property Manager',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		leasing_agent: {
			name: 'Leasing Agent',
			color: 'green',
			icon: 'building' as UnifiedIconName,
		},
		viewer: {
			name: 'Viewer',
			color: 'gray',
			icon: 'person' as UnifiedIconName,
		},
		tenant: {
			name: 'Tenant',
			color: 'purple',
			icon: 'person' as UnifiedIconName,
		},
	}

	return (
		roleInfo[role as keyof typeof roleInfo] || {
			name: role,
			color: 'gray',
			icon: 'person' as UnifiedIconName,
		}
	)
}
